import React, { useState, useEffect } from 'react';
import './App.css';
import Header from './components/Header';
import HeroSection from './components/HeroSection';
import BrowseCategories from './components/BrowseCategories';
import ExploreProducts from './components/ExploreProducts';
import NewArrivals from './components/NewArrivals';
import ServicesBanner from './components/ServicesBanner';
import Footer from './components/Footer';
import AboutUs from './components/AboutUs';
import ContactUs from './components/ContactUs';
import ProductDetail from './components/ProductDetail';
import Login from './components/Login';
import Register from './components/Register';
import AdminPanel from './components/AdminPanel';
import Cart from './components/Cart';
import Checkout from './components/Checkout';
import ThankYou from './components/ThankYou';
import UserProfile from './components/UserProfile';
import OrderHistory from './components/OrderHistory';
import RmaModal from './components/RmaModal';
import WishList from './components/WishList';
import WarrantyPolicy from './components/WarrantyPolicy';
import CmsPage from './components/CmsPage';
import BlogList from './components/BlogList';
import BlogPostView from './components/BlogPostView';
import LandingPageView from './components/LandingPageView';
import NotFound from './components/NotFound';
import { applySeo, fetchSiteSettings, fetchCmsPage } from './utils/seoManager';
import { initClickTracking } from './utils/analytics';

function App() {
  const [currentPath, setCurrentPath] = useState(window.location.pathname);
  const [user, setUser] = useState(null);
  const [isRmaOpen, setIsRmaOpen] = useState(false);
  const [siteSettings, setSiteSettings] = useState({ useSlugUrls: false, useCmsPages: false });
  const [cmsPages, setCmsPages] = useState({});

  useEffect(() => {
    fetchSiteSettings().then(setSiteSettings);
    initClickTracking();
  }, []);

  useEffect(() => {
    const handleLocationChange = () => {
      setCurrentPath(window.location.pathname);
      window.scrollTo(0, 0);
    };

    const loadSession = () => {
      const session = localStorage.getItem('user_session');
      if (session) {
        setUser(JSON.parse(session));
      } else {
        setUser(null);
      }
    };

    const refreshSession = async () => {
      const session = localStorage.getItem('user_session');
      if (!session) return;
      try {
        const parsed = JSON.parse(session);
        if (!parsed.email) return;
        const res = await fetch('/api/auth/profile', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: parsed.email })
        });
        if (res.ok) {
          const data = await res.json();
          if (data.user) {
            const updated = { ...parsed, ...data.user };
            localStorage.setItem('user_session', JSON.stringify(updated));
            setUser(updated);
            window.dispatchEvent(new Event('auth_change'));
          }
        }
      } catch (err) {
        console.error('Session refresh failed:', err);
      }
    };

    loadSession();
    refreshSession();

    const handleOpenRma = () => setIsRmaOpen(true);

    window.addEventListener('popstate', handleLocationChange);
    window.addEventListener('hashchange', handleLocationChange);
    window.addEventListener('storage', loadSession);
    window.addEventListener('auth_change', loadSession);
    window.addEventListener('open_rma_modal', handleOpenRma);

    return () => {
      window.removeEventListener('popstate', handleLocationChange);
      window.removeEventListener('hashchange', handleLocationChange);
      window.removeEventListener('storage', loadSession);
      window.removeEventListener('auth_change', loadSession);
      window.removeEventListener('open_rma_modal', handleOpenRma);
    };
  }, []);

  const isAboutPage = currentPath === '/about-us';
  const isContactPage = currentPath === '/contact-us';
  const isLoginPage = currentPath === '/login';
  const isRegisterPage = currentPath === '/register' || currentPath === '/signup';
  const isAdminPage = currentPath === '/admin' && user && user.email === 'smarttelecom.nl@gmail.com';
  const isCartPage = currentPath === '/cart';
  const isCheckoutPage = currentPath === '/checkout';
  const isThankYouPage = currentPath === '/thank-you';
  const isProfilePage = currentPath === '/profile' || currentPath === '/my-account';
  const isOrderHistoryPage = currentPath === '/order-history' || currentPath === '/orders';
  const isWishListPage = currentPath === '/wish-list' || currentPath === '/wishlist';
  const isWarrantyPage = currentPath === '/warranty-policy' || currentPath === '/warranty';
  const isBlogListPage = currentPath === '/blog';
  const isBlogPostPage = currentPath.startsWith('/blog/') && currentPath !== '/blog';
  const blogSlug = isBlogPostPage ? currentPath.replace('/blog/', '') : '';
  const isLandingPage = currentPath.startsWith('/lp/') || currentPath.startsWith('/campaign/');
  const landingSlug = isLandingPage ? currentPath.split('/').pop() : '';
  const isCategoryPage = currentPath.startsWith('/category/');
  const isBrandPage = currentPath.startsWith('/brand/');
  const isProductPage = currentPath.startsWith('/product/');
  let productId = '';
  if (isProductPage) {
    productId = currentPath.split('/').pop() || '';
  }

  const isHomePage = currentPath === '/' || currentPath === '';
  const knownRoutes = isAboutPage || isContactPage || isLoginPage || isRegisterPage || isAdminPage
    || isCartPage || isCheckoutPage || isThankYouPage || isProfilePage || isOrderHistoryPage
    || isWishListPage || isWarrantyPage || isBlogListPage || isBlogPostPage || isLandingPage
    || isCategoryPage || isBrandPage || isProductPage || isHomePage;
  const is404 = !knownRoutes;

  useEffect(() => {
    if (siteSettings.useCmsPages) {
      const keys = ['about-us', 'contact-us', 'warranty-policy', 'home'];
      keys.forEach(async (key) => {
        const page = await fetchCmsPage(key);
        if (page) setCmsPages((prev) => ({ ...prev, [key]: page }));
      });
    }
  }, [siteSettings.useCmsPages]);

  useEffect(() => {
    if (isProductPage) return;

    let seo = {
      seoTitle: 'Smart Telecom Store | Premium Mobile & Telecom Accessories',
      metaDescription: 'Welcome to Smart Telecom Store. Explore our wide range of premium smartphones, mobile accessories, chargers, cables, and telecom solutions.',
      keywords: 'smart telecom store, mobile accessories, chargers, cables, smartphones, telecom products',
    };

    if (isAboutPage) {
      const cms = cmsPages['about-us'];
      if (cms && siteSettings.useCmsPages) seo = { seoTitle: cms.seoTitle, metaDescription: cms.metaDescription, ...cms };
      else seo = { seoTitle: 'About Us | Smart Telecom Store', metaDescription: 'Learn more about Smart Telecom Store.' };
    } else if (isContactPage) {
      const cms = cmsPages['contact-us'];
      if (cms && siteSettings.useCmsPages) seo = { seoTitle: cms.seoTitle, metaDescription: cms.metaDescription, ...cms };
      else seo = { seoTitle: 'Contact Us | Smart Telecom Store', metaDescription: 'Get in touch with Smart Telecom Store.' };
    } else if (isWarrantyPage) {
      const cms = cmsPages['warranty-policy'];
      if (cms && siteSettings.useCmsPages) seo = { seoTitle: cms.seoTitle, metaDescription: cms.metaDescription, ...cms };
      else seo = { seoTitle: 'Warranty Policy | Smart Telecom Store', metaDescription: 'Warranty and RMA policy.' };
    } else if (isLoginPage) {
      seo = { seoTitle: 'Login | Smart Telecom Store', metaDescription: 'Log in to your account.', robotsIndex: false };
    } else if (isRegisterPage) {
      seo = { seoTitle: 'Register | Smart Telecom Store', metaDescription: 'Create a new account.' };
    } else if (isAdminPage) {
      seo = { seoTitle: 'Admin Dashboard', metaDescription: 'Admin panel', robotsIndex: false };
    } else if (isCartPage || isCheckoutPage) {
      seo = { seoTitle: `${isCartPage ? 'Cart' : 'Checkout'} | Smart Telecom Store`, robotsIndex: false };
    } else if (isThankYouPage) {
      seo = { seoTitle: 'Thank You | Smart Telecom Store', metaDescription: 'Order placed successfully.' };
    } else if (is404) {
      seo = { seoTitle: '404 | Smart Telecom Store', metaDescription: 'Page not found.', robotsIndex: false };
    }

    applySeo(seo);
  }, [currentPath, isAboutPage, isContactPage, isLoginPage, isRegisterPage, isAdminPage, isCartPage, isCheckoutPage, isThankYouPage, isProductPage, isWarrantyPage, is404, cmsPages, siteSettings.useCmsPages]);

  const [landingHideHeader, setLandingHideHeader] = useState(false);
  useEffect(() => {
    if (!isLandingPage || !landingSlug) { setLandingHideHeader(false); return; }
    fetch(`/api/landing-pages/slug/${landingSlug}`)
      .then((r) => r.ok ? r.json() : null)
      .then((p) => setLandingHideHeader(!!p?.hideHeaderFooter));
  }, [isLandingPage, landingSlug]);

  const renderContent = () => {
    if (isAboutPage) {
      const cms = cmsPages['about-us'];
      if (cms && siteSettings.useCmsPages) return <CmsPage page={cms} fallback={<AboutUs />} />;
      return <AboutUs />;
    }
    if (isContactPage) {
      const cms = cmsPages['contact-us'];
      if (cms && siteSettings.useCmsPages) return <CmsPage page={cms} fallback={<ContactUs />} />;
      return <ContactUs />;
    }
    if (isLoginPage) return <Login />;
    if (isRegisterPage) return <Register />;
    if (isAdminPage) return <AdminPanel />;
    if (isProductPage) return <ProductDetail productId={productId} siteSettings={siteSettings} />;
    if (isCartPage) return <Cart />;
    if (isCheckoutPage) return <Checkout />;
    if (isThankYouPage) return <ThankYou />;
    if (isProfilePage) return <UserProfile />;
    if (isOrderHistoryPage) return <OrderHistory />;
    if (isWishListPage) return <WishList siteSettings={siteSettings} />;
    if (isWarrantyPage) {
      const cms = cmsPages['warranty-policy'];
      if (cms && siteSettings.useCmsPages) return <CmsPage page={cms} fallback={<WarrantyPolicy />} />;
      return <WarrantyPolicy />;
    }
    if (isBlogListPage) return <BlogList />;
    if (isBlogPostPage) return <BlogPostView slug={blogSlug} />;
    if (isLandingPage) return <LandingPageView slug={landingSlug} />;
    if (isCategoryPage) return <ExploreProducts categorySlug={currentPath.replace('/category/', '')} />;
    if (isBrandPage) return <ExploreProducts brandSlug={currentPath.replace('/brand/', '')} />;
    if (is404) return <NotFound />;

    const homeCms = cmsPages.home;
    if (homeCms && siteSettings.useCmsPages && homeCms.sections?.length) {
      return (
        <>
          <CmsPage page={homeCms} />
          <BrowseCategories />
          <ExploreProducts siteSettings={siteSettings} />
          <NewArrivals />
          <ServicesBanner />
        </>
      );
    }
    return (
      <>
        <HeroSection />
        <BrowseCategories />
        <ExploreProducts siteSettings={siteSettings} />
        <NewArrivals />
        <ServicesBanner />
      </>
    );
  };

  const hideChrome = isLandingPage && landingHideHeader;

  return (
    <div className="App">
      {!hideChrome && <Header siteSettings={siteSettings} />}
      {renderContent()}
      {!hideChrome && <Footer />}
      <RmaModal isOpen={isRmaOpen} onClose={() => setIsRmaOpen(false)} />
      <div className="whatsapp-float-container">
        <svg viewBox="0 0 100 100" className="whatsapp-text-ring">
          <path id="whatsapp-text-path" d="M 50,50 m -36,0 a 36,36 0 1,1 72,0 a 36,36 0 1,1 -72,0" fill="none" />
          <text fill="#be1e2d" fontSize="9" fontWeight="700" letterSpacing="1.5">
            <textPath href="#whatsapp-text-path" startOffset="0%">
              • CHAT WITH US • HELP LINE • SMART TELECOM
            </textPath>
          </text>
        </svg>
        <a
          href="https://wa.me/31687748358"
          target="_blank"
          rel="noopener noreferrer"
          className="whatsapp-float-btn"
          aria-label="Contact us on WhatsApp"
          data-track="whatsapp_click"
        >
          <svg width="34" height="34" viewBox="0 0 24 24" fill="currentColor">
            <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946C.06 5.348 5.397.01 12.008.01c3.202.001 6.212 1.246 8.477 3.514 2.266 2.268 3.507 5.28 3.505 8.484-.004 6.657-5.34 11.997-11.953 11.997-2.005-.001-3.973-.502-5.724-1.455L0 24zm6.59-4.846c1.66.986 3.296 1.506 4.905 1.508 5.482.002 9.948-4.468 9.95-9.953.003-2.657-1.02-5.153-2.883-7.019C16.762 1.82 14.269.789 11.618.789c-5.485 0-9.95 4.466-9.952 9.953-.001 1.83.479 3.619 1.393 5.215l-.99 3.61 3.708-.973zm12.38-5.321c-.305-.153-1.804-.89-2.083-.99-.279-.103-.483-.153-.686.153-.203.306-.787.99-.965 1.193-.177.202-.355.228-.66.076-.305-.153-1.288-.475-2.455-1.517-.908-.81-1.52-1.81-1.698-2.115-.178-.306-.019-.471.133-.622.137-.137.305-.355.457-.533.153-.178.203-.305.305-.509.102-.203.05-.381-.025-.533-.076-.153-.686-1.653-.94-2.26-.247-.594-.5-.513-.686-.523-.178-.01-.381-.01-.584-.01-.203 0-.533.076-.813.381-.28.305-1.067 1.042-1.067 2.541 0 1.499 1.092 2.946 1.244 3.149.153.203 2.15 3.284 5.207 4.602.727.314 1.294.502 1.737.643.73.232 1.394.2 1.917.12.583-.087 1.804-.737 2.058-1.45.254-.713.254-1.323.178-1.45-.076-.127-.28-.203-.585-.356z"/>
          </svg>
        </a>
      </div>
    </div>
  );
}

export default App;
